package org.teddapp.project.ui.notes

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Note
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.teddOverlayShadow

/** Une phrase secrète trop courte se devine : huit caractères au moins. */
private const val MIN_PASSPHRASE = 8

@Composable
private fun SecretField(value: String, placeholder: String, onChange: (String) -> Unit, onDone: () -> Unit) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    BasicTextField(
        value = value,
        onValueChange = onChange,
        singleLine = true,
        visualTransformation = PasswordVisualTransformation(),
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password, imeAction = ImeAction.Done),
        keyboardActions = KeyboardActions(onDone = { onDone() }),
        textStyle = TeddType.Body.copy(color = TeddTokens.Ink),
        cursorBrush = SolidColor(TeddTokens.Ink),
        modifier = Modifier
            .fillMaxWidth()
            .height(40.dp)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, TeddTokens.LineStrong, shape),
        decorationBox = { inner ->
            Box(Modifier.padding(horizontal = TeddSpace.S3), contentAlignment = Alignment.CenterStart) {
                if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkSubtle, style = TeddType.Body)
                inner()
            }
        },
    )
}

/**
 * Verrouiller une note : la phrase, deux fois. Elle n'est écrite nulle part ; perdue, la
 * note ne s'ouvre plus. On le dit avant, pas après.
 */
@Composable
internal fun LockNoteDialog(model: NotesModel, note: Note, draft: NoteDraft, onClose: () -> Unit) {
    var first by remember { mutableStateOf("") }
    var second by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var busy by remember { mutableStateOf(false) }
    fun submit() {
        error = when {
            first.length < MIN_PASSPHRASE -> "Huit caractères au moins."
            first != second -> "Les deux phrases ne sont pas identiques."
            else -> null
        }
        if (error != null || busy) return
        busy = true
        model.lockNote(note, draft.title, draft.body.text.toString(), first) { refusal ->
            busy = false
            if (refusal == null) onClose() else error = refusal
        }
    }
    val shape = RoundedCornerShape(TeddTokens.RLg)
    Dialog(onDismissRequest = { if (!busy) onClose() }, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .padding(TeddSpace.S4)
                .widthIn(max = 420.dp)
                .teddOverlayShadow(shape)
                .background(TeddTokens.SurfaceOverlay, shape)
                .border(1.dp, TeddTokens.Line, shape)
                .padding(TeddSpace.S6),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S3),
        ) {
            Text("Verrouiller la note", style = TeddType.Title2, color = TeddTokens.Ink)
            Text(
                "Le texte sera chiffré ici et sur le serveur ; le titre reste visible. " +
                    "La phrase n'est gardée nulle part : oubliée, la note ne s'ouvrira plus.",
                style = TeddType.Caption,
                color = TeddTokens.InkMuted,
            )
            SecretField(first, "Phrase secrète", { first = it; error = null }, onDone = ::submit)
            SecretField(second, "La même, encore", { second = it; error = null }, onDone = ::submit)
            error?.let { Text(it, style = TeddType.Caption, color = TeddTokens.Danger) }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                TeddButton(
                    text = if (busy) "Chiffrement…" else "Verrouiller",
                    onClick = ::submit,
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Md,
                    icon = "lock",
                    modifier = Modifier.weight(1f),
                )
                TeddButton(text = "Annuler", onClick = { if (!busy) onClose() }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Md)
            }
        }
    }
}

/** Le texte d'une note verrouillée, à ouvrir par sa phrase. */
@Composable
internal fun LockedNoteBody(model: NotesModel, note: Note) {
    var passphrase by remember(note.id) { mutableStateOf("") }
    var error by remember(note.id) { mutableStateOf<String?>(null) }
    var busy by remember(note.id) { mutableStateOf(false) }
    fun submit() {
        if (passphrase.isEmpty() || busy) return
        busy = true
        model.unlockNote(note, passphrase) { ok ->
            busy = false
            if (!ok) error = "Ce n'est pas la bonne phrase."
        }
    }
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 240.dp)
            .background(TeddTokens.SurfaceSunken, shape)
            .padding(TeddSpace.S6),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S3, Alignment.CenterVertically),
    ) {
        TeddIcon(name = "lock", size = 28.dp, color = TeddTokens.InkMuted)
        Text("Note verrouillée", style = TeddType.Title2, color = TeddTokens.Ink)
        if (!NoteLock.available) {
            Text(
                "Elle s'ouvre sur Android et sur ordinateur ; le chiffrement n'est pas encore disponible ici.",
                style = TeddType.Caption,
                color = TeddTokens.InkMuted,
            )
            return@Column
        }
        Box(Modifier.widthIn(max = 320.dp)) {
            SecretField(passphrase, "Phrase secrète", { passphrase = it; error = null }, onDone = ::submit)
        }
        error?.let { Text(it, style = TeddType.Caption, color = TeddTokens.Danger) }
        TeddButton(
            text = if (busy) "Ouverture…" else "Ouvrir",
            onClick = ::submit,
            kind = TeddButtonKind.Primary,
            size = TeddButtonSize.Md,
            icon = "lock",
        )
        Text(
            "Elle se referme dès que tu la quittes.",
            style = TeddType.Caption,
            color = TeddTokens.InkSubtle,
        )
    }
}
